import type {FC} from 'react';

import {observer} from 'mobx-react';
import {OverlayScrollbarsComponent} from 'overlayscrollbars-react';

import {useI18n} from '@yourcoach/i18n';

import {CalendarDatePicker} from '../../../components/CalendarDatePicker';
import {Clickable} from '../../../uikit/Clickable';
import {LoadingSpinner} from '../../../uikit/LoadingSpinner';
import {Text} from '../../../uikit/Text';
import {payloadActionCreator} from '../../../utils/events';
import {logger} from '../../../utils/logger';
import {TestID} from '../../../utils/test-utils/data-test-id';
import {type BookConferenceStore} from '../BookConference.store';
import {ConferenceDuration} from '../components';
import {BookButton} from '../components/BookButton';
import {CoachesSelect} from '../components/CoachSelect';
import {FakeTimeslotsRow} from '../components/FakeTimeslotsRow';
import {Layout} from '../components/Layout';
import {Timeslot} from '../components/Timeslot';
import {TimeslotsEmptyState} from '../components/TimeslotsEmptyState';

interface Props {
  isRoundedBookButton?: boolean;
  onBook?: () => void;
  shouldHideBookButtonOnVerticalLayout?: boolean;
  store: BookConferenceStore;
}

export const BookConferenceContainer: FC<Props> = observer(
  ({
    isRoundedBookButton,
    onBook,
    shouldHideBookButtonOnVerticalLayout = true,
    store,
  }) => {
    const {t} = useI18n();
    const onBookButtonClick = async () => {
      logger.event(
        store.strategy.conferenceToRescheduleId
          ? 'reschedule_session_tap'
          : 'schedule_session_tap',
      );

      try {
        await store.book();

        onBook?.();
      } catch (error) {
        // handled in store
      }
    };

    return (
      <Layout.Container
        shouldHideBookButtonOnVerticalLayout={
          shouldHideBookButtonOnVerticalLayout
        }
      >
        <Layout.Aside.Container>
          <Layout.Aside.Header>
            <Layout.Aside.Title>
              {t('shared:book_conference.choose_day')}
            </Layout.Aside.Title>
            <Layout.Aside.LegendContainer>
              <Layout.Aside.Legend>
                <Layout.Aside.Bullet color={store.availableSlotColor} />
                <Text color={'text3'} variant={'label.la'}>
                  -{' '}
                  {
                    /**
                     * @i18n_parser
                     * t('shared:book_conference.old_coach')
                     * t('shared:book_conference.available_days')
                     */
                    t(
                      `shared:book_conference.${
                        store.strategy.hasCoachCoverage
                          ? 'old_coach'
                          : 'available_days'
                      }`,
                    )
                  }
                </Text>
              </Layout.Aside.Legend>
              {store.strategy.hasCoachCoverage ? (
                <Layout.Aside.Legend>
                  <Layout.Aside.Bullet
                    color={store.availableSlotColor}
                    stripped
                  />
                  <Text color={'text3'} variant={'label.la'}>
                    - {t('shared:book_conference.temporary_coach')}
                  </Text>
                </Layout.Aside.Legend>
              ) : null}
            </Layout.Aside.LegendContainer>
          </Layout.Aside.Header>
          <Layout.Aside.Body>
            {store.shouldShowAvailableConferences ? (
              <Layout.Aside.FooterCell>
                <Text>
                  {t('shared:book_conference.available_sessions')}
                  {': '}
                </Text>
                <Text weight={'bold'}>{store.availableConferences}</Text>
              </Layout.Aside.FooterCell>
            ) : null}
            {store.strategy.isShowCoachSelect ? (
              <CoachesSelect store={store.strategy.coaches} />
            ) : null}
            <Layout.Aside.CalendarContainer>
              <CalendarDatePicker
                markedDates={store.markedDates}
                max={store.calendar.maxDate}
                min={store.calendar.minDate}
                onChange={store.calendar.onDateChange}
                scheme="secondary"
                testID={TestID.bookConference.block.calendar}
                value={store.calendar.selectedDate}
              />
            </Layout.Aside.CalendarContainer>
          </Layout.Aside.Body>
        </Layout.Aside.Container>
        <Layout.Aside.Container>
          <Layout.Aside.Header>
            <Layout.Aside.Title>
              {t('shared:book_conference.choose_time')}
            </Layout.Aside.Title>

            <Layout.TimeMeta.Container>
              <ConferenceDuration duration={store.conferenceDuration} />
              {store.strategy.shouldShowTimezone ? (
                <Layout.TimeMeta.Timezone>{`${t('shared:time_zone')}: ${store.currentUserTimezoneString}`}</Layout.TimeMeta.Timezone>
              ) : null}
            </Layout.TimeMeta.Container>
          </Layout.Aside.Header>
          <Layout.Aside.Body>
            <Layout.Timeslots.Container>
              <FakeTimeslotsRow />
              {store.timeSlots.initialLoad.isDone &&
              !store.timeSlots.busy.getReason('makeSlots') ? (
                <>
                  {!store.isSessionLimitReachedForToday &&
                  store.timeSlots.todaySlots.length ? (
                    <OverlayScrollbarsComponent
                      defer
                      options={{scrollbars: {autoHide: 'scroll'}}}
                    >
                      <Layout.Timeslots.List>
                        {store.todayTimeslots
                          .reduce((acc, row) => [...acc, ...row], [])
                          .map(item => (
                            <Timeslot
                              isDisabled={item.isActive.isFalse}
                              isSelected={item.isSelected.isTrue}
                              key={item.label}
                              label={item.label}
                              onClick={() =>
                                store.timeSlots.onSlotSelect(
                                  payloadActionCreator(item),
                                )
                              }
                            />
                          ))}
                      </Layout.Timeslots.List>
                    </OverlayScrollbarsComponent>
                  ) : (
                    <TimeslotsEmptyState
                      isSessionsLimitReached={
                        store.isSessionLimitReachedForToday
                      }
                    />
                  )}
                </>
              ) : (
                <Layout.Aside.SpinnerContainer>
                  <LoadingSpinner />
                </Layout.Aside.SpinnerContainer>
              )}
            </Layout.Timeslots.Container>
            <Layout.BookButton.HorizontalContainer
              shouldHide={shouldHideBookButtonOnVerticalLayout}
            >
              <BookButton
                detail={store.bookingDateToString}
                disabled={!store.timeSlots.selected}
                isLoading={store.busy.getReason('book')}
                isRounded={isRoundedBookButton}
                onClick={onBookButtonClick}
                testID={TestID.bookConference.button.confirm}
                title={store.title}
              />
            </Layout.BookButton.HorizontalContainer>
          </Layout.Aside.Body>
          {store.isShowBuyMoreButton ? (
            <Layout.Aside.Footer>
              <Layout.Aside.FooterCell>
                <Clickable testID={TestID.bookConference.button.buyMore}>
                  <Text color={'text5'}>
                    {t('shared:book_conference.buy_more')}
                  </Text>
                </Clickable>
              </Layout.Aside.FooterCell>
            </Layout.Aside.Footer>
          ) : null}
        </Layout.Aside.Container>
      </Layout.Container>
    );
  },
);
